import { useState, useRef, useEffect, type ReactNode } from "react";
import Markdown from "react-markdown";
import { useStudentStore } from "../../hooks/use-student-store";
import { Send, Loader2, X, BookOpen, ClipboardCheck, CheckCircle2 } from "lucide-react";
import { cn } from "../../lib/utils";
import { ChatMode } from "@live-writing/models";

const ChatInterface = () => {
  const {
    chatMode,
    clarifyMessages,
    quizMessages,
    sendMessage,
    isSending,
    isQuizComplete,
    completeQuiz,
    exitChat,
    isGeneratingResults,
    quizResults,
  } = useStudentStore();

  const messages = chatMode === ChatMode.QUIZ ? quizMessages : clarifyMessages;
  const [input, setInput] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (chatMode && inputRef.current) {
      inputRef.current.focus();
    }
  }, [chatMode]);

  useEffect(() => {
    if (isQuizComplete && !quizResults && !isGeneratingResults) {
      completeQuiz();
    }
  }, [isQuizComplete, quizResults, isGeneratingResults, completeQuiz]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isSending) return;

    const message = input.trim();
    setInput("");
    await sendMessage(message);
  };

  if (!chatMode) return null;

  const isQuiz = chatMode === ChatMode.QUIZ;
  const Icon = isQuiz ? ClipboardCheck : BookOpen;
  const title = isQuiz ? "Quiz" : "Clarify with AI";
  const subtitle = isQuiz ? "Test your understanding" : "Ask questions about the materials";

  return (
    <div className={cn(
      "rounded-xl overflow-hidden border",
      isQuiz ? "border-amber-200 bg-amber-50/30" : "border-teal-200 bg-teal-50/30"
    )}>
      <div className={cn(
        "px-4 py-3 border-b flex items-center justify-between",
        isQuiz ? "bg-amber-50 border-amber-100" : "bg-teal-50 border-teal-100"
      )}>
        <div className="flex items-center gap-2">
          <Icon className={cn("h-5 w-5", isQuiz ? "text-amber-600" : "text-teal-600")} />
          <div>
            <h3 className={cn("font-semibold text-sm", isQuiz ? "text-amber-900" : "text-teal-900")}>{title}</h3>
            <p className={cn("text-xs", isQuiz ? "text-amber-600" : "text-teal-600")}>{subtitle}</p>
          </div>
        </div>
        <button
          onClick={exitChat}
          className="p-1.5 rounded-md hover:bg-black/5 transition-colors"
        >
          <X className="h-4 w-4 text-gray-500" />
        </button>
      </div>

      <div className="h-72 overflow-y-auto p-4 space-y-3 bg-white/50">
        {messages.length === 0 && !isSending && (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <Icon className={cn("h-10 w-10 mb-2", isQuiz ? "text-amber-300" : "text-teal-300")} />
            <p className="text-gray-500 text-sm">
              {isQuiz ? "Quiz starting..." : "Ask any question about the materials"}
            </p>
          </div>
        )}
        {messages.map((msg, index) => (
          <div
            key={index}
            className={cn("flex", msg.role === "user" ? "justify-end" : "justify-start")}
          >
            <div
              className={cn(
                "max-w-[80%] rounded-lg px-3 py-2 text-sm",
                msg.role === "user"
                  ? isQuiz
                    ? "bg-amber-500 text-white"
                    : "bg-teal-500 text-white"
                  : "bg-gray-100 text-gray-800"
              )}
            >
              {msg.role === "user" ? (
                <p className="whitespace-pre-wrap leading-relaxed">{msg.content}</p>
              ) : (
                <Markdown
                  components={{
                    p: ({ children }: { children?: ReactNode }) => <p className="mb-2 last:mb-0 leading-relaxed">{children}</p>,
                    ul: ({ children }: { children?: ReactNode }) => <ul className="list-disc pl-4 mb-2 space-y-1">{children}</ul>,
                    ol: ({ children }: { children?: ReactNode }) => <ol className="list-decimal pl-4 mb-2 space-y-1">{children}</ol>,
                    li: ({ children }: { children?: ReactNode }) => <li className="leading-relaxed">{children}</li>,
                    strong: ({ children }: { children?: ReactNode }) => <strong className="font-semibold">{children}</strong>,
                    em: ({ children }: { children?: ReactNode }) => <em className="italic">{children}</em>,
                    code: ({ children }: { children?: ReactNode }) => <code className="bg-gray-200 px-1 rounded text-xs">{children}</code>,
                  }}
                >
                  {msg.content}
                </Markdown>
              )}
            </div>
          </div>
        ))}
        {isSending && (
          <div className="flex justify-start">
            <div className="bg-gray-100 rounded-lg px-3 py-2">
              <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {isQuizComplete ? (
        <div className="p-4 border-t border-green-100 bg-green-50 space-y-4">
          {isGeneratingResults && (
            <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-green-200">
              <div className="flex-1">
                <p className="text-green-700 text-sm font-medium">Generating your results...</p>
                <p className="text-green-600 text-xs">Analysing your quiz responses</p>
              </div>
              <Loader2 className="h-4 w-4 animate-spin text-green-500" />
            </div>
          )}
          {quizResults && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-green-700">
                <CheckCircle2 className="h-5 w-5" />
                <p className="font-medium">Quiz Complete!</p>
              </div>
              {(quizResults.feedback as { summary?: string })?.summary && (
                <p className="text-gray-700 text-sm leading-relaxed">
                  {(quizResults.feedback as { summary?: string }).summary}
                </p>
              )}
              {quizResults.questions_for_class && quizResults.questions_for_class.length > 0 && (
                <div className="mt-3">
                  <p className="text-sm font-medium text-gray-900 mb-2">Questions to Ponder for Class</p>
                  <ul className="space-y-1.5">
                    {quizResults.questions_for_class.map((question, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm">
                        <span className="text-green-500 mt-0.5">•</span>
                        <span className="text-gray-700">{question}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <form onSubmit={handleSubmit} className={cn(
          "p-3 border-t flex gap-2",
          isQuiz ? "border-amber-100 bg-amber-50/50" : "border-teal-100 bg-teal-50/50"
        )}>
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={isQuiz ? "Type your answer..." : "Ask a question..."}
            disabled={isSending}
            className={cn(
              "flex-1 h-10 px-3 rounded-lg border bg-white text-sm focus:outline-none focus:ring-2 transition-all",
              isQuiz
                ? "border-amber-200 focus:border-amber-300 focus:ring-amber-100"
                : "border-teal-200 focus:border-teal-300 focus:ring-teal-100"
            )}
          />
          <button
            type="submit"
            disabled={isSending || !input.trim()}
            className={cn(
              "w-10 h-10 rounded-lg flex items-center justify-center disabled:opacity-50 transition-colors",
              isQuiz
                ? "bg-amber-500 hover:bg-amber-600 text-white"
                : "bg-teal-500 hover:bg-teal-600 text-white"
            )}
          >
            {isSending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </button>
        </form>
      )}
    </div>
  );
};

export default ChatInterface;
